import React, { useState, useEffect } from 'react'
import api from '../lib/api'
import { setUser, getUser } from '../lib/auth'

export default function EditProfileModal({ isOpen, onClose, onUpdated }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const user = getUser()
      setName(user?.name || '')
      setEmail(user?.email || '')
      setError(null)
      setSuccess(null)
      setSubmitted(false)
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleClose = () => {
    setError(null)
    setSuccess(null)
    onClose()
  }

  const isFieldMissing = (val) => submitted && !val

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitted(true)

    if (!name || !email) return

    setError(null)
    setSuccess(null)
    setSaving(true)

    try {
      const res = await api.patch('/profile', { user: { name, email } })
      setUser(res.data.user)
      if (res.data.email_changed) {
        setSuccess('Profile updated. A confirmation email has been sent to your new address. Your email will update once confirmed.')
      } else {
        setSuccess('Profile updated successfully.')
      }
      onUpdated(res.data.user)
    } catch (err) {
      setError(err.response?.data?.errors?.join(', ') || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const inputClass = "w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-surface border-[1.5px] border-ink shadow-lg w-full max-w-sm mx-4">
        <div className="px-6 py-4 border-b border-line">
          <h3 className="text-xl font-extrabold">Edit Profile</h3>
        </div>

        <div className="px-6 py-4">
          {error && (
            <div className="text-sm text-accent-dark bg-accent/10 border border-accent/40 p-3 mb-3">{error}</div>
          )}
          {success && (
            <div className="text-sm text-ink bg-surface p-3 rounded mb-3">{success}</div>
          )}

          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`${inputClass} ${isFieldMissing(name) ? 'border-accent' : ''}`}
                />
                {isFieldMissing(name) && <p className="text-xs text-accent-dark mt-1">Required</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${inputClass} ${isFieldMissing(email) ? 'border-accent' : ''}`}
                />
                {isFieldMissing(email) && <p className="text-xs text-accent-dark mt-1">Required</p>}
                <p className="text-xs text-gray-400 mt-1">Changing your email requires confirmation.</p>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          ) : (
            <div className="flex justify-end pt-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
